import produce from "immer";
import { shuffle } from "./sorts";
import equal from "fast-deep-equal";
import { colors } from "../constants/colors";

const getType = (data) => Object.keys(data || {})[0];

const evaluate = (base, attempt) => {
  const typeBase = getType(base);
  const typeAttempt = getType(attempt);

  if (typeBase !== typeAttempt || !typeBase || !typeAttempt) return undefined;

  const type = typeBase;

  let func = (attempt, base, onFailure) => attempt;
  switch (type) {
    case "LESSON_DISCOVER_SENTENCE":
      func = produce((draftState, base, onFailure) => {
        draftState.forEach((item, index) => {
          const wordsAttempt = item.words;
          const wordsBase = base[index]?.words;

          let state = "";
          if (equal(wordsAttempt, wordsBase)) state = "SUCCESS";
          else {
            state = "FAILURE";
            onFailure();
          }

          draftState[index].state = state;
        });
      });
      break;
    case "LESSON_DISCOVER_WORD":
      func = produce((draftState, base, onFailure) => {
        draftState.forEach((item, index) => {
          const refsAttempt = item.refs;
          const refsBase = base[index]?.refs;

          let state = "";
          if (equal(refsAttempt, refsBase)) state = "SUCCESS";
          else {
            state = "FAILURE";
            onFailure();
          }

          draftState[index].state = state;
        });
      });
      break;
    case "LESSON_LIST_ORDER":
      func = produce((draftState, base, onFailure) => {
        draftState.forEach((item, index) => {
          const itemAttempt = item;
          const itemBase = base[index];

          let state = "";
          if (itemAttempt.text === itemBase.text) state = "SUCCESS";
          else {
            state = "FAILURE";
            onFailure();
          }

          draftState[index].state = state;
        });
      });
      break;
    case "LESSON_SELECT":
      func = produce((draftState, base, onFailure) => {
        draftState.forEach((item, index, array) => {
          let alternativeCorrectAttempt = undefined;
          let alternativeCorrectBase = undefined;

          for (let i = 0; i < item.alternatives.length; i++) {
            const alternative = item.alternatives[i];
            if (alternative.selected) {
              alternativeCorrectAttempt = {
                ...alternative,
                index: i,
              };
              break;
            }
          }

          for (let i = 0; i < base[index].alternatives?.length; i++) {
            const alternative = base[index].alternatives[i];
            if (alternative.selected) {
              alternativeCorrectBase = alternative;
              break;
            }
          }

          let state = "";
          if (
            alternativeCorrectAttempt?.text === alternativeCorrectBase?.text &&
            alternativeCorrectAttempt?.audio === alternativeCorrectBase?.audio
          )
            state = "SUCCESS";
          else {
            state = "FAILURE";
            onFailure();
          }

          if (alternativeCorrectAttempt?.index >= 0)
            array[index].alternatives[alternativeCorrectAttempt.index].state =
              state;
          else {
            item.alternatives.forEach(
              (alternative, alternativeIndex) =>
                (array[index].alternatives[alternativeIndex].state = "FAILURE")
            );
            onFailure();
          }
        });
      });
      break;
    case "LESSON_UNION": {
      func = (attempt, base, onFailure) => {
        return produce(attempt, (draftState) => {
          draftState.forEach((item, index, array) => {
            if (item?.pair === null) {
              array[index].state = "FAILURE";
              onFailure();
              return;
            }

            const baseIndex = base.findIndex(
              (baseItem) =>
                baseItem?.text === item?.text && baseItem?.image === item?.image
            );

            if (baseIndex === -1) {
              array[index].state = "FAILURE";
              onFailure();
              return;
            }

            const pairBase = base[base[baseIndex].pair];
            const pairItem = array[item.pair];

            if (
              pairBase &&
              pairItem &&
              pairBase.text === pairItem.text &&
              pairBase.image === pairItem.image
            )
              array[index].state = "SUCCESS";
            else {
              array[index].state = "FAILURE";
              onFailure();
            }
          });
        });
      };
      break;
    }

    case "LESSON_UNION_WITH_BOX": {
      const evalSector = produce(
        (draftSector1, baseSector1, attemptSector2, baseSector2, onFailure) => {
          draftSector1.forEach((item, index, array) => {
            if (item?.pair === null) {
              array[index].state = "FAILURE";
              onFailure();
              return;
            }

            const selectedA = item;
            const selectedB = attemptSector2[item.pair];

            const itemsA_array = baseSector1;
            const allowedB_array = [];
            for (let index = 0; index < itemsA_array.length; index++) {
              const itemA = itemsA_array[index];
              if(itemA.text.trim() === selectedA.text.trim()){
                const allowedB = baseSector2[itemA.pair];
                allowedB_array.push(allowedB);
              }
            }

            const isFailed = allowedB_array.some((allowedB) => allowedB.text.trim() === selectedB.text.trim()) === false;

            if(isFailed){
              array[index].state = "FAILURE";
              onFailure();
              return;
            } else {
              array[index].state = "SUCCESS";
            }
          });
        }
      );

      func = produce((draftState, base, onFailure) => {
        draftState.alternatives = [
          evalSector(
            draftState.alternatives[0],
            base.alternatives[0],
            draftState.alternatives[1],
            base.alternatives[1],
            onFailure
          ),
          evalSector(
            draftState.alternatives[1],
            base.alternatives[1],
            draftState.alternatives[0],
            base.alternatives[0],
            onFailure
          ),
        ];
      });
      break;
    }
    default:
      break;
  }

  let hasFailures = false;

  const data = func(attempt[type], base[type], () => {
    hasFailures = true;
  });

  return {
    state: hasFailures ? "FAILURE" : "SUCCESS",
    data: { [typeAttempt]: data },
  };
};

const resolve = (base, attempt) => {
  const typeBase = getType(base);
  const typeAttempt = getType(attempt);

  if (typeBase !== typeAttempt || !typeBase || !typeAttempt) return undefined;

  const type = typeBase;

  let func = (attempt, base) => attempt;
  switch (type) {
    case "LESSON_DISCOVER_SENTENCE":
      func = produce((draftState, base) => {
        draftState.forEach((item, index, array) => {
          array[index].words = base[index].words;
          array[index].state = "SUCCESS";
        });
      });
      break;
    case "LESSON_DISCOVER_WORD":
      func = produce((draftState, base) => {
        draftState.forEach((item, index, array) => {
          array[index].refs = base[index].refs;
          array[index].state = "SUCCESS";
        });
      });
      break;
    case "LESSON_LIST_ORDER":
      func = produce((draftState, base) => {
        draftState.forEach((item, index, array) => {
          array[index].text = base[index].text;
          array[index].state = "SUCCESS";
        });
      });
      break;
    case "LESSON_SELECT":
      func = produce((draftState, base) => {
        draftState.forEach((item, index, array) => {
          let optionCorrect = undefined;

          for (let i = 0; i < base[index].alternatives.length; i++) {
            const option = base[index].alternatives[i];
            if (option.selected) {
              optionCorrect = option;
              break;
            }
          }

          item.alternatives.forEach((option, optionIndex) => {
            if (
              option.text === optionCorrect.text &&
              option.audio === optionCorrect.audio
            ) {
              array[index].alternatives[optionIndex].selected = true;
              array[index].alternatives[optionIndex].state = "SUCCESS";
            } else {
              array[index].alternatives[optionIndex].selected = false;
              array[index].alternatives[optionIndex].state = null;
            }
          });
        });
      });
      break;
    case "LESSON_UNION": {
      func = (attempt, base) => {
        const palette = colors.map((c) => ({ color: c, selected: false }));

        const getUnselectedColor = () =>
          palette.find((i) => i.selected === false)?.color;

        const selectColor = (color) => {
          const i = palette.findIndex((i) => i.color === color);
          if (i >= 0) palette[i].selected = true;
        };

        const colored = {};

        return produce(attempt, (draftState) => {
          draftState.forEach((item, index, array) => {
            if (colored[index]) return;
            const indexBase = base.findIndex(
              (i) => i.text === item.text && i.image === item.image
            );

            const itemPairBase = base[base[indexBase].pair];

            const indexPairItem = array.findIndex(
              (i) =>
                i.text === itemPairBase.text && i.image === itemPairBase.image
            );

            const color = getUnselectedColor();
            array[index].state = "SUCCESS";
            array[index].color = color;
            array[indexPairItem].state = "SUCCESS";
            array[indexPairItem].color = color;
            colored[index] = true;
            colored[indexPairItem] = true;
            selectColor(color);
          });
        });
      };
      break;
    }

    case "LESSON_UNION_WITH_BOX": {
      func = produce((draftState, base) => {
        const palette = colors.map((c) => ({ color: c, selected: false }));

        const getUnselectedColor = () =>
          palette.find((i) => i.selected === false)?.color;

        const selectColor = (color) => {
          const i = palette.findIndex((i) => i.color === color);
          if (i >= 0) palette[i].selected = true;
        };

        draftState.alternatives[0].forEach((item, index, array) => {
          const color = getUnselectedColor();
          array[index].color = color;
          array[index].state = "SUCCESS";
          selectColor(color);
        });

        draftState.alternatives[1].forEach((item, index, array) => {
          const baseItem = base.alternatives[1].find(
            (i) => i.text === item.text
          );
          const baseItemPair = base.alternatives[0][baseItem.pair];

          const itemPair = draftState.alternatives[0].find(
            (i) => i.text === baseItemPair.text
          );

          array[index].color = itemPair.color;
          array[index].state = "SUCCESS";
        });
      });
      break;
    }
    default:
      break;
  }

  return {
    data: { [typeAttempt]: func(attempt[type], base[type]) },
  };
};

const sort = (state) => {
  const type = getType(state);

  if (!type) return undefined;

  let func = (state) => state;

  switch (type) {
    case "LESSON_DISCOVER_SENTENCE":
      func = produce((draftState) => {
        draftState.forEach((item, index, array) => {
          array[index].words = shuffle(item.words);
          array[index].state = null;
        });
      });
      break;
    case "LESSON_DISCOVER_WORD":
      func = produce((draftState) => {
        draftState.forEach((item, index, array) => {
          array[index].refs = item.refs?.map((ref) => shuffle(ref));
          array[index].state = null;
        });
      });
      break;
    case "LESSON_LIST_ORDER":
      func = (data) =>
        produce(shuffle(data), (draftState) => {
          draftState.forEach((item, index, array) => {
            array[index].state = null;
          });
        });

      break;
    case "LESSON_SELECT":
      func = produce((draftState) => {
        draftState.forEach((item, index, array) => {
          array[index].alternatives = shuffle(item.alternatives || []);
          array[index].alternatives.forEach((item, index, array) => {
            array[index].selected = false;
            array[index].state = null;
          });
        });
      });
      break;
    case "LESSON_UNION":
      func = (data) =>
        produce(shuffle(data), (draftState) => {
          draftState.forEach((item, index, array) => {
            array[index].pair = null;
            array[index].color = null;
            array[index].state = null;
          });
        });
      break;
    case "LESSON_UNION_WITH_BOX": {
      const sortSector = (data) =>
        produce(shuffle(data), (draftState) => {
          draftState.forEach((item, index, array) => {
            array[index].pair = null;
            array[index].color = null;
            array[index].state = null;
          });
        });
      func = produce((draftState) => {
        draftState.alternatives = [
          sortSector(draftState.alternatives[0]),
          sortSector(draftState.alternatives[1]),
        ];
      });
      break;
    }
    default:
      break;
  }

  return {
    [type]: func(state[type]),
  };
};

export { evaluate, sort, resolve };
